import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import { splitParticipants } from "@/shared/collapsibleParticipants.ts";
import { CHEVRON_DOWN_PATH, CHEVRON_UP_PATH } from "@/shared/morphIcons.ts";
import type { ViewParticipant } from "@/types/artifacts-view.ts";
import { MorphIcon } from "morphicons/react";
import { type ReactNode, useState } from "react";

/**
 * A read-only list of people that starts collapsed once it is long enough to
 * be a wall rather than a list.
 *
 * An email drafted as a reply-all to a large thread can carry a hundred
 * recipients; rendered whole, the artifact's own Send and Discard controls end
 * up far below the fold (BIG-7523). Showing the first few and counting the rest
 * keeps the card the size of the decision it is asking for.
 *
 * Collapsed is the default on every render, deliberately: the list is
 * incidental to the artifact, and someone who expanded one earlier is not
 * asking every later draft to open long.
 *
 * Read-only lists only. While a list is being edited, every person on it stays
 * on screen — that is exactly when the user is checking who is about to receive
 * something.
 */
export const CollapsibleParticipants = ({
  participants,
  renderParticipant,
  className,
}: {
  participants: ViewParticipant[];
  /** Each call site builds its own pill, so only the splitting is shared. */
  renderParticipant: (participant: ViewParticipant) => ReactNode;
  /** Layout of the container — callers stack or wrap as their row needs. */
  className?: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const { visible, hiddenCount, collapsible } = splitParticipants(participants, expanded);

  return (
    <div className={cn("flex flex-row flex-wrap items-center gap-1", className)}>
      {visible.map(renderParticipant)}
      {collapsible && (
        <Button
          type="button"
          onClick={() => setExpanded(!expanded)}
          variant="outlinePopover"
          // Matches the pills it sits among: they are `h-6 ... text-xs` too, so
          // the toggle reads as the last item in the list rather than a control
          // bolted onto the end of it.
          size="2xs"
          className="gap-1 text-muted-foreground"
          aria-expanded={expanded}
          data-statsig-name="artifact-participants-toggle"
        >
          {expanded ? "Show less" : `+${hiddenCount} more`}
          {/* `reducedMotion="user"` rather than the library's "never" default:
              the chevron should still change, just without the morph, for
              someone who asked the OS for less motion. */}
          <MorphIcon
            icon={expanded ? CHEVRON_UP_PATH : CHEVRON_DOWN_PATH}
            spring="snappy"
            size={12}
            reducedMotion="user"
          />
        </Button>
      )}
    </div>
  );
};
